/** @jsxImportSource @emotion/react */

import { FC, useState } from 'react'
import Typography from '@mui/material/Typography'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import IconButton from '@mui/material/IconButton'
import { ReactComponent as InputIcon } from 'assets/icons/input.svg'
import { findOneQuestionPassageBank_findOneQuestionPassageBank_questionPassages as QuestionPassage } from '__generatedTypes__/findOneQuestionPassageBank'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import * as classes from '../../styles'
import { EditTitleForm } from '../EditTitleForm'

type Props = {
  index: number
  expanded: string | null
  questionPassage: QuestionPassage
  onSuccessfulEdit?: (questionPassage: QuestionPassage, index: number) => void
  onEditClick: (e: React.SyntheticEvent<SVGSVGElement>, questionPassage: QuestionPassage, index: number) => void
  onDeleteClick: (e: React.SyntheticEvent<SVGSVGElement>, questionPassage: QuestionPassage, index: number) => void
}

export const AccordionSummaryContent: FC<Props> = ({
  index,
  expanded,
  questionPassage,
  onSuccessfulEdit,
  onEditClick,
  onDeleteClick,
}) => {
  const [isEditing, setIsEditing] = useState(false)

  const handleEditTitleButtonClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  const handleStopEditingTitle = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsEditing(false)
  }

  const handleSuccessfulEdit = (questionPassage: QuestionPassage) => {
    setIsEditing(false)
    onSuccessfulEdit?.(questionPassage, index)
  }

  return (
    <>
      <div css={[classes.accordionSummaryBlock, isEditing && classes.accordionSummaryEditBlock]}>
        <Typography variant="body3" css={classes.index} data-testid="question-passage-index">
          {index + 1}
        </Typography>

        {isEditing ? (
          <EditTitleForm
            questionPassage={questionPassage}
            onStopEditingTitle={handleStopEditingTitle}
            onSuccessfulEdit={handleSuccessfulEdit}
          />
        ) : (
          <>
            <Typography variant="body2" css={classes.title} data-testid="question-passage-title">
              {questionPassage.title}
            </Typography>
            <IconButton
              onClick={handleEditTitleButtonClick}
              css={classes.iconButton}
              data-testid="question-passage-edit-title-button"
            >
              <InputIcon css={classes.inputIcon} />
            </IconButton>
          </>
        )}
      </div>
      <div css={classes.accordionSummaryBlock}>
        {!isEditing && (
          <>
            <EditIcon
              css={[classes.icon, classes.iconEdit]}
              onClick={(e) => onEditClick(e, questionPassage, index)}
              data-testid="question-passage-edit-button"
            />
            <CloseRoundedIcon
              css={[classes.icon, classes.iconCross]}
              onClick={(e) => onDeleteClick(e, questionPassage, index)}
              data-testid="question-passage-delete-button"
            />
          </>
        )}
        <ExpandMoreRoundedIcon
          css={[classes.iconChevron, expanded === questionPassage.id && classes.iconChevronOpen]}
          data-testid="question-passage-expand-icon"
        />
        <DnDIcon />
      </div>
    </>
  )
}
